import { UserAvatarControllerService } from "@9amhealth/openapi";
import { Cubit } from "blac";
import type { SyntheticEvent } from "react";
import { addSentryBreadcrumb } from "src/lib/addSentryBreadcrumb";
import envVariables from "src/lib/envVariables";
import { createUnifiedFileCache } from "src/lib/fileCache";
import reportErrorSentry from "src/lib/reportErrorSentry";
import { authenticationState, toast, userState } from "src/state/state";
import type { HTMLInputEvent } from "src/types/common";

interface AvatarState {
  id: string;
  path?: string;
}

const AVATAR_CACHE_PREFIX = "avatar:";

const avatarFileCache = createUnifiedFileCache({
  onError: (error, { operation }) => {
    reportErrorSentry(error, { context: `avatarFileCache.${operation}` });
  }
});

export default class AvatarCubit extends Cubit<AvatarState> {
  constructor(id = "") {
    super({
      id
    });

    if (id) {
      void this.loadAvatar();
    }
  }

  public readonly uploadAvatar = async (
    originalEvent: SyntheticEvent<HTMLInputElement>
  ): Promise<void> => {
    const event = originalEvent as unknown as HTMLInputEvent;
    try {
      for (const file of event.target.files ?? []) {
        const blobUrl = URL.createObjectURL(file as Blob);

        this.emit({
          ...this.state,
          path: blobUrl
        });

        await UserAvatarControllerService.createAvatar(file);
        await this.loadAvatar({ clear: true });
      }
      addSentryBreadcrumb("avatar", `Successfully uploaded avatar`);
    } catch (e: unknown) {
      reportErrorSentry(e);
      toast.error("error.uploadAvatar");
    }

    (originalEvent.target as HTMLInputElement).value = "";
  };

  private static pendingRequests = new Map<string, Promise<string | null>>();
  private static loadedAvatarUrls = new Map<string, string>();

  private static fetchAvatarBlob = async (
    userId: string
  ): Promise<Blob | null> => {
    const response = await fetch(
      `${envVariables.API_BASE_URL}/v1/users/${userId}/avatar`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${authenticationState.accessToken}`
        }
      }
    );

    if (response.ok) {
      return response.blob();
    }
    return null;
  };

  private static loadAvatarForUser = async (
    userId: string
  ): Promise<string | null> => {
    const pending = AvatarCubit.pendingRequests.get(userId);
    if (pending) {
      return pending;
    }

    const loadedUrl = AvatarCubit.loadedAvatarUrls.get(userId);
    if (loadedUrl) {
      return loadedUrl;
    }

    const currentUserId = authenticationState.activeUserId;
    if (!currentUserId) {
      return null;
    }

    const promise = (async (): Promise<string | null> => {
      try {
        const cacheKey = `${AVATAR_CACHE_PREFIX}${userId}`;
        const cached = await avatarFileCache.getCachedFile(
          cacheKey,
          currentUserId
        );

        if (cached) {
          const blobUrl = URL.createObjectURL(cached.blob);
          AvatarCubit.loadedAvatarUrls.set(userId, blobUrl);
          return blobUrl;
        }

        const blob = await AvatarCubit.fetchAvatarBlob(userId);
        if (!blob) {
          return null;
        }

        await avatarFileCache.cacheFile(
          cacheKey,
          currentUserId,
          blob,
          `avatar-${userId}`,
          blob.type || "image/jpeg"
        );

        const blobUrl = URL.createObjectURL(blob);
        AvatarCubit.loadedAvatarUrls.set(userId, blobUrl);
        return blobUrl;
      } catch {
        addSentryBreadcrumb("avatar", `Avatar not found for user`);
        return null;
      } finally {
        AvatarCubit.pendingRequests.delete(userId);
      }
    })();

    AvatarCubit.pendingRequests.set(userId, promise);
    return promise;
  };

  static clearAvatarCache = async (userId: string): Promise<void> => {
    AvatarCubit.loadedAvatarUrls.delete(userId);
    const currentUserId = authenticationState.activeUserId;
    if (currentUserId) {
      const cacheKey = `${AVATAR_CACHE_PREFIX}${userId}`;
      await avatarFileCache.removeCachedFile(cacheKey, currentUserId);
    }
  };

  private readonly loadAvatar = async (
    options: { clear?: boolean } = {}
  ): Promise<void> => {
    const userId = this.state.id || userState.state.userData?.id;
    if (!userId || userId === "SYSTEM") {
      return;
    }

    if (options.clear) {
      await AvatarCubit.clearAvatarCache(userId);
    }

    const path = await AvatarCubit.loadAvatarForUser(userId);
    if (path) {
      this.emit({
        ...this.state,
        path
      });
    }
  };
}
